import { FC, useMemo, ReactNode } from 'react';
import {
    Grid,
} from 'antd';
import ResponsiveTable from '@/components/atoms/ResponsiveTable';
import PhoneExpandedContent from './PhoneExpandedContent';
import EmailExpandedContent from './EmailExpandedContent';
import ApiExpandedContent from './ApiExpandedContent';
import { communicationColumns } from './communicationColumns';
import type { Snapshot } from 'valtio';
import { CommunicationRecord, LabelId, LabelSet, SignifyOrderStatusCheck } from '#types';
import { EmailFormData } from './EmailPopoverForm';
import { PhoneCallFormData } from './PhoneCallPopoverForm';

const { useBreakpoint } = Grid;


type CommunicationLogDisplayMode = 'default' | 'compact';

interface CommunicationTableProps {
    records: CommunicationRecord[];
    loading?: boolean;
    disabled?: boolean;
    /** Drops the actions column entirely, the one state that legitimately has no
     *  buttons. A `disabled` log keeps them, greyed. */
    pdfExportMode?: boolean;
    logdisplay?: CommunicationLogDisplayMode;
    onDelete?: (record: CommunicationRecord, deleteAttachments: boolean) => void;
    onEmailSave?: (record: CommunicationRecord, data: EmailFormData) => boolean | Promise<boolean>;
    onPhoneSave?: (record: CommunicationRecord, data: PhoneCallFormData) => boolean | Promise<boolean>;
    onLabelsSave?: (record: CommunicationRecord, labelIds: LabelId[]) => boolean | Promise<boolean>;
    onCheckOrderStatus?: (record: CommunicationRecord) => Promise<SignifyOrderStatusCheck | undefined>;
    labelSet?: Snapshot<LabelSet>;
    controls?: () => ReactNode;
}

const CommunicationTable: FC<CommunicationTableProps> = ({
    records,
    loading = false,
    disabled = false,
    pdfExportMode = false,
    logdisplay = 'default',
    onDelete,
    onEmailSave,
    onPhoneSave,
    onLabelsSave,
    onCheckOrderStatus,
    labelSet,
    controls,
}) => {
    const screens = useBreakpoint();

    const columns = useMemo(
        () => communicationColumns({
            disabled,
            pdfExportMode,
            fixedActions: screens.lg,
            logdisplay,
            onDelete,
            onEmailSave,
            onPhoneSave,
            onLabelsSave,
            onCheckOrderStatus,
            labelSet,
        }),
        [disabled, pdfExportMode, screens.lg, logdisplay, onDelete, onEmailSave, onPhoneSave, onLabelsSave, onCheckOrderStatus, labelSet],
    );

    const isRowExpandable = (record: CommunicationRecord) => {
        if (record.type === 'phone') {
            return Boolean(record.notes);
        }
        if (record.type === 'api') {
            return true;
        }
        if (record.type === 'attachment') {
            return false;
        }
        return Boolean(
            record.body ||
            record.hasAttachments ||
            (record.outboundAttachmentLabels && record.outboundAttachmentLabels.length > 0) ||
            (record.cc && record.cc.length > 0) ||
            (record.bcc && record.bcc.length > 0)
        );
    };

    const sortedRecords = useMemo(
        () => [...records].sort((a, b) => {
            const aTime = a.timestamp?.toMillis ? a.timestamp.toMillis() : 0;
            const bTime = b.timestamp?.toMillis ? b.timestamp.toMillis() : 0;
            return bTime - aTime;
        }),
        [records],
    );

    const showFooter = sortedRecords.length > 10;

    return (
        <ResponsiveTable<CommunicationRecord>
            dataSource={sortedRecords}
            columns={columns}
            loading={loading}
            expandable={{
                expandedRowRender: (record) => {
                    if (record.type === 'phone') {
                        return <PhoneExpandedContent record={record} />;
                    }
                    if (record.type === 'api') {
                        return <ApiExpandedContent record={record} />;
                    }
                    /** Narrows type for TypeScript: prevents AttachmentCommunicationRecord flowing to EmailExpandedContent */
                    if (record.type === 'attachment') {
                        return null;
                    }
                    return <EmailExpandedContent record={record} />;
                },
                rowExpandable: (record) => isRowExpandable(record),
            }}
            pagination={false}
            style={{
                width: '100%',
            }}
            size="small"
            rowKey={(record) => record.id}
            title={controls}
            footer={showFooter ? controls : undefined}
            locale={{ emptyText: 'No communication log entries' }}
        />
    );
};

export default CommunicationTable;
